import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("gtf_token");
    if (!token) { setLoading(false); return; }

    let cancelled = false;
    const fetchMe = async () => {
      // Retry up to 3 times with backoff to survive transient prod hiccups
      // (Cloudflare 502, backend cold-start, brief network drop).
      let lastErr = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const r = await api.get("/auth/me");
          if (!cancelled) {
            setUser(r.data);
            setLoading(false);
          }
          return;
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          if (status === 401 || status === 403) {
            // Server actively rejects — token is bad, wipe it.
            localStorage.removeItem("gtf_token");
            break;
          }
          // Transient — wait and retry.
          await new Promise((res) => setTimeout(res, 400 * (attempt + 1)));
        }
      }
      if (!cancelled) {
        console.warn("[GTF] /auth/me failed after retries:", lastErr?.response?.status || lastErr?.message);
        setLoading(false);
      }
    };
    fetchMe();
    return () => { cancelled = true; };
  }, []);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    localStorage.setItem("gtf_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("gtf_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("gtf_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const ROLE_THEMES = {
  farmer: {
    name: "Farmer",
    primary: "#16A34A",
    primaryDark: "#15803D",
    ring: "ring-emerald-400/40",
    text: "text-emerald-700",
    bg: "bg-emerald-600",
    bgSoft: "bg-emerald-50",
    border: "border-emerald-200",
    gradient: "from-emerald-600 to-emerald-800",
    shadow: "premium-shadow",
    hero: "https://images.unsplash.com/photo-1628492058844-589eb5dc6a35?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBmYXJtZXIlMjBwb3J0cmFpdCUyMGZpZWxkfGVufDB8fHx8MTc4NDY4NDQ3Nnww&ixlib=rb-4.1.0&q=85",
    categories: ["Farm Products", "Farm Services", "Farm Equipment", "Farm Labour", "Seeds", "Organic", "Vegetables", "Milk", "Eggs", "Rice", "Land", "Plot", "House", "Vehicle", "Tractor", "Livestock", "Other"],
    dashCards: [
      { key: "listings_count", label: "Products Posted" },
      { key: "orders_received", label: "Orders Received" },
      { key: "products_sold", label: "Products Sold" },
      { key: "revenue", label: "Revenue", isCurrency: true },
    ],
    actions: [
      { key: "post_product", label: "Post Product" },
      { key: "post_service", label: "Post Farm Service" },
      { key: "orders", label: "My Orders" },
      { key: "responses", label: "Responses" },
      { key: "messages", label: "Messages" },
      { key: "marketplace", label: "Marketplace" },
    ],
  },
  day_worker: {
    name: "Day Worker",
    primary: "#2563EB",
    primaryDark: "#1D4ED8",
    ring: "ring-blue-400/40",
    text: "text-blue-700",
    bg: "bg-blue-600",
    bgSoft: "bg-blue-50",
    border: "border-blue-200",
    gradient: "from-blue-600 to-blue-800",
    shadow: "blue-shadow",
    hero: "https://images.pexels.com/photos/7807035/pexels-photo-7807035.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    categories: ["Electrician", "Plumber", "Painter", "Mechanic", "Driver", "Photographer", "Cook", "Cleaner", "House Maid", "Gardener", "Car Wash", "AC Repair", "Mobile Repair", "Computer Repair", "Construction", "Labour", "Welding", "Security Guard", "Delivery", "Home Services", "Vehicle Rent", "Other"],
    dashCards: [
      { key: "listings_count", label: "Services Posted" },
      { key: "orders_placed", label: "Jobs Applied" },
      { key: "products_sold", label: "Jobs Completed" },
      { key: "revenue", label: "Total Earnings", isCurrency: true },
    ],
    actions: [
      { key: "marketplace", label: "Marketplace" },
      { key: "post_service", label: "Post Service" },
      { key: "orders", label: "My Jobs" },
      { key: "responses", label: "Responses" },
      { key: "messages", label: "Messages" },
    ],
  },
  investor: {
    name: "Investor",
    primary: "#FBBF24",
    primaryDark: "#B45309",
    ring: "ring-amber-400/50",
    text: "text-amber-700",
    bg: "bg-amber-500",
    bgSoft: "bg-amber-50",
    border: "border-amber-200",
    gradient: "from-amber-500 to-amber-700",
    shadow: "gold-shadow",
    hero: "https://images.pexels.com/photos/8463151/pexels-photo-8463151.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    categories: ["Startup Ideas", "Business Plans", "Film Projects", "Agriculture Projects", "Factory Projects", "Technology Projects", "Funding Requests", "Investment Opportunities", "Revenue Sharing", "Equity Deals", "Real Estate", "Land Deals", "Franchise", "Other"],
    dashCards: [
      { key: "listings_count", label: "Investment Requests" },
      { key: "orders_placed", label: "Projects Viewed" },
      { key: "responses", label: "Responses" },
      { key: "revenue", label: "Portfolio Value", isCurrency: true },
    ],
    actions: [
      { key: "post_product", label: "Post Investment" },
      { key: "marketplace", label: "Find Startups" },
      { key: "orders", label: "My Deals" },
      { key: "responses", label: "Responses" },
      { key: "messages", label: "Chat" },
    ],
  },
};
